/**
 * Exact visual tokens lifted from the legacy calendar and day modal.
 *
 * These are hard-coded hex values rather than Tailwind classes because that is
 * how the legacy code wrote them — inline `style="background:#581c87"` — and
 * matching the old UI exactly was the explicit requirement. Keeping them in one
 * table means the whole palette can be adjusted in one place if a shade turns
 * out to be off against the live legacy build.
 *
 * Source lines in doctor_dashboard.js are noted per entry.
 */

/* ==========================================================================
   Slot cells — day details modal
   ========================================================================== */

/** Booked slot, by status. From viewDayDetails (:13790–13801). */
export const SLOT_BOOKED = {
  completed: { bg: '#1e3a8a', border: '#3b82f6', text: '#60a5fa', icon: 'check' },
  pending: { bg: '#7f1d1d', border: '#ef4444', text: '#fca5a5', icon: 'clock' },
  confirmed: { bg: '#581c87', border: '#a855f7', text: '#c084fc', icon: null },
};

export const slotBookedStyle = (status) =>
  SLOT_BOOKED[status] || SLOT_BOOKED.confirmed;

/** A booking made by an admin gets a white glow. From viewDayDetails (:13802). */
export const ADMIN_GLOW =
  '0 0 10px rgba(255,255,255,0.5), 0 0 20px rgba(255,255,255,0.3)';

/**
 * A booking the admin discounted, in the clinic view.
 *
 * The three status colours (blue/red/purple) answer "where is this booking in
 * its life", and a discounted visit is not a fourth state of that — it is a
 * booking whose *money* differs from what the clinic's own price list says.
 * White is the one value not already spoken for, and it is bright enough that
 * a doctor scanning a full day finds the exception without reading any of it.
 *
 * Kept distinct from ADMIN_GLOW: that marks who made the booking, this marks
 * what was charged, and a booking can be either, both, or neither.
 */
export const SLOT_ADMIN_DISCOUNT = {
  bg: 'rgba(255,255,255,0.92)',
  border: '#ffffff',
  text: '#0f172a',
  glow: '0 0 12px rgba(255,255,255,0.75), 0 0 26px rgba(255,255,255,0.4)',
};

/** Free regular slot — dark green, dashed. From openDayModal (:4396). */
export const SLOT_FREE = {
  bg: '#0d3b2e',
  border: '1.5px dashed #2ecc71',
  timeColor: '#86efac', // green-300
  labelColor: '#22c55e', // green-500
};

/** Free extra-hours slot — gold. From openDayModal (:4427). */
export const SLOT_FREE_EXTRA = {
  bg: '#2d1f00',
  border: '1.5px solid #7d5a00',
  timeColor: '#fde047', // yellow-300
  labelColor: '#eab308', // yellow-500
};

/* ==========================================================================
   Day modal chrome — from viewDayDetails (:13998–14030)
   ========================================================================== */

export const MODAL_SURFACE = { bg: '#0b111e', border: 'rgba(255,255,255,0.05)' };

/** "إضافة ساعات إضافية" — muted purple. */
export const BTN_EXTRA_HOURS = { bg: '#281b3a', border: '#4a3364' };

/** "إضافة عرض" — bright purple with glow. */
export const BTN_OFFER = { bg: '#ac5af0', glow: '0 0 20px rgba(172,90,240,0.3)' };

/** The drag hint bar. */
export const DRAG_HINT = { bg: '#00262b', border: '#0c4f55', text: '#0cb7c4' };

/** "إلغاء اليوم" — dark red. */
export const BTN_CANCEL_DAY = { bg: '#1a0505', border: '#5c1a1a', text: '#f87171' };

/* ==========================================================================
   Month calendar day cells — from renderGlobalCalendar (:7669–8140)
   ========================================================================== */

/** Per-clinic tint. The first working clinic's colour drives the cell. */
export const CLINIC_TINTS = {
  cyan: [6, 182, 212],
  emerald: [16, 185, 129],
  purple: [168, 85, 247],
  orange: [249, 115, 22],
  rose: [244, 63, 94],
};

/**
 * A clinic's colour as [r, g, b].
 *
 * Live data holds two spellings. The legacy clinic editor stored a palette
 * *name* ("cyan", "emerald"); the React editor stores a hex string ("#06b6d4").
 * Looking a hex value up in the name table misses, so without this every
 * React-created clinic tinted the calendar cyan regardless of its colour.
 */
export function resolveClinicRgb(color) {
  if (Array.isArray(color) && color.length === 3) return color;
  if (typeof color === 'string') {
    const named = CLINIC_TINTS[color.toLowerCase()];
    if (named) return named;

    const hex = color.trim().replace('#', '');
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    if (/^[0-9a-f]{6}$/i.test(full)) {
      return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16),
      ];
    }
  }
  return CLINIC_TINTS.cyan;
}

/** The same colour as a `#rrggbb` string, for borders and dots. */
export function clinicHex(color) {
  const [r, g, b] = resolveClinicRgb(color);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Cell tint, brighter the more of the day is still free.
 *
 * The legacy formula: background opacity 0.05→0.25 and border 0.2→0.35, scaled
 * by the proportion of slots still available. A busy day therefore looks darker,
 * which is the at-a-glance signal the whole grid is built around.
 */
export function clinicTintStyle(colorName, availabilityPercent) {
  const rgb = resolveClinicRgb(colorName);
  const bgOpacity = 0.05 + (availabilityPercent / 100) * 0.2;
  const borderOpacity = 0.2 + (availabilityPercent / 100) * 0.15;
  return {
    backgroundColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${bgOpacity})`,
    borderColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${borderOpacity})`,
  };
}

/** A day with no clinic working. */
export const DAY_IDLE = {
  backgroundColor: 'rgba(30, 41, 59, 0.3)',
  borderColor: 'rgba(255, 255, 255, 0.05)',
};

/** A day carrying pending (unpaid) bookings — red tint. */
export const DAY_PENDING = {
  backgroundColor: 'rgba(239,68,68,0.12)',
  borderColor: 'rgba(239,68,68,0.45)',
};

/** A day whose bookings are all confirmed — indigo tint. */
export const DAY_CONFIRMED = {
  backgroundColor: 'rgba(99,102,241,0.13)',
  borderColor: 'rgba(139,92,246,0.45)',
};

/** Offer pill — orange→pink gradient. */
export const BADGE_OFFER =
  'linear-gradient(135deg, #f97316, #ec4899)';
export const BADGE_OFFER_SHADOW = '0 2px 10px rgba(249,115,22,0.55)';

/** Extra-hours pill — purple gradient. */
export const BADGE_EXTRA =
  'linear-gradient(135deg, #8b5cf6, #c084fc, #a855f7)';
export const BADGE_EXTRA_SHADOW = '0 2px 10px rgba(139,92,246,0.55)';

/** Pending-count bubble, top corner of a day cell. */
export const BADGE_PENDING = 'linear-gradient(135deg,#ef4444,#b91c1c)';
export const BADGE_PENDING_SHADOW = '0 2px 8px rgba(239,68,68,0.7)';

/* ==========================================================================
   Live view — from renderScheduleManagement (:3405–3455) and the two builders
   ========================================================================== */

export const LIVE_TOGGLE_SECRETARY =
  'linear-gradient(135deg,#0ea5e9,#06b6d4)';
export const LIVE_TOGGLE_SECRETARY_SHADOW = '0 4px 15px rgba(6,182,212,0.35)';

export const LIVE_TOGGLE_DOCTOR = 'linear-gradient(135deg,#8b5cf6,#7c3aed)';
export const LIVE_TOGGLE_DOCTOR_SHADOW = '0 4px 15px rgba(139,92,246,0.35)';

/** Counter tiles. Secretary shows three, doctor shows two. */
export const COUNTER = {
  done: { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.2)', text: '#34d399' },
  total: { bg: 'rgba(6,182,212,0.1)', border: 'rgba(6,182,212,0.2)', text: '#22d3ee' },
  remaining: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', text: '#fbbf24' },
  donePurple: { bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.2)', text: '#a78bfa' },
};

/** Queue row states in the secretary list. */
export const QUEUE_ROW = {
  active:
    'border:1.5px solid rgba(16,185,129,0.7); background:rgba(16,185,129,0.08); box-shadow:0 0 20px rgba(16,185,129,0.15);',
  past: 'opacity:0.45; border:1px solid rgba(255,255,255,0.05);',
  upcoming: 'border:1px solid rgba(255,255,255,0.06); background:rgba(255,255,255,0.02);',
};

/** The doctor's three action buttons. */
export const ACTION_BUTTONS = {
  medicalCv: 'linear-gradient(135deg,#6366f1,#4f46e5)',
  prescription: 'linear-gradient(135deg,#0891b2,#0e7490)',
  complete: 'linear-gradient(135deg,#10b981,#059669)',
};

/** Patient card header — red when the booking was made by an admin. */
export const PATIENT_HEADER = {
  normal: 'linear-gradient(135deg,rgba(139,92,246,0.12),rgba(109,40,217,0.06))',
  admin:
    'linear-gradient(135deg,rgba(239,68,68,0.15),rgba(153,27,27,0.1))',
};

export const ARABIC_MONTHS = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

/** The grid starts on Saturday, as the legacy header row does. */
export const WEEKDAY_HEADERS = [
  'السبت', 'الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة',
];

/**
 * Leading blank cells before day 1.
 * JS getDay() is Sun=0…Sat=6; the grid starts Saturday, so (getDay + 1) % 7.
 */
export const leadingBlanks = (firstDayIndex) => (firstDayIndex + 1) % 7;
